/**
 * Parses text with optional line-prefix markers.
 *
 * Works for single-line or multi-line input.
 *
 * Template example (Localizely):
 *
 * #bullet# First bullet item
 * #bullet# Second bullet item
 * Optional trailing line without prefix
 *
 * To add a new symbol later:
 * 1. Add it to TEXT_LINE_PREFIXES
 * 2. Add a matching field to ParsedPrefixedText
 * 3. Register a handler in PREFIX_HANDLERS
 */
export const TEXT_LINE_PREFIXES = {
  bullet: '#bullet#',
} as const;

export type PrefixedLineType = keyof typeof TEXT_LINE_PREFIXES;

/** @deprecated Use TEXT_LINE_PREFIXES.bullet */
export const DEFAULT_BULLET_LINE_PREFIX = TEXT_LINE_PREFIXES.bullet;

export interface ParsedPrefixedText {
  bullets: string[];
  trailingText?: string;
  plainText?: string;
}

export interface ParsePrefixedTextOptions {
  prefixes?: Partial<Record<PrefixedLineType, string>>;
}

interface MutableParsedPrefixedText {
  bullets: string[];
  trailingLines: string[];
}

const PREFIX_HANDLERS: Record<
  PrefixedLineType,
  (result: MutableParsedPrefixedText, content: string) => void
> = {
  bullet: (result, content) => {
    result.bullets.push(content);
  },
};

const resolvePrefixes = (
  overrides?: Partial<Record<PrefixedLineType, string>>
): Record<PrefixedLineType, string> => ({
  ...TEXT_LINE_PREFIXES,
  ...overrides,
});

const findMatchingPrefix = (
  line: string,
  prefixes: Record<PrefixedLineType, string>
): { type: PrefixedLineType; prefix: string } | null => {
  for (const [type, prefix] of Object.entries(prefixes) as [
    PrefixedLineType,
    string,
  ][]) {
    if (line.startsWith(prefix)) {
      return { type, prefix };
    }
  }
  return null;
};

export const parsePrefixedText = (
  text: string,
  { prefixes: prefixOverrides }: ParsePrefixedTextOptions = {}
): ParsedPrefixedText => {
  const prefixes = resolvePrefixes(prefixOverrides);
  const lines = text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  const result: MutableParsedPrefixedText = {
    bullets: [],
    trailingLines: [],
  };

  lines.forEach((line) => {
    const match = findMatchingPrefix(line, prefixes);

    if (match) {
      const content = line.slice(match.prefix.length).trimStart();
      PREFIX_HANDLERS[match.type](result, content);
      return;
    }

    result.trailingLines.push(line);
  });

  if (result.bullets.length === 0) {
    return { bullets: [], plainText: text.trim() };
  }

  return {
    bullets: result.bullets,
    trailingText:
      result.trailingLines.length > 0
        ? result.trailingLines.join('\n')
        : undefined,
  };
};
